import { useState, useEffect, useCallback } from "react";
import EventIcon from "@mui/icons-material/Event";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import { CircularProgress } from "@mui/material";
import { useTheme } from "../../../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useMoodleV2 } from "../../../contexts/moodle";

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const DIAS_SEMANA = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

/**
 * Convierte un timestamp Unix (segundos) a string "YYYY-MM-DD"
 */
const unixToIso = (ts) => {
  const d = new Date(ts * 1000);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDisplayDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  return `${day} ${MESES[month - 1]} ${year}`;
};

const getDaysUntil = (isoDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate + "T00:00:00");
  const diff = Math.ceil((target - today) / 86400000);
  if (diff < 0) return { label: "Vencido", color: "#ef4444" };
  if (diff === 0) return { label: "Hoy", color: "#f59e0b" };
  if (diff === 1) return { label: "Mañana", color: "#f59e0b" };
  if (diff <= 3) return { label: `En ${diff} días`, color: "#f59e0b" };
  return { label: `En ${diff} días`, color: "#22c55e" };
};

/**
 * Determina el tipo legible del evento según el modname de Moodle.
 * core_calendar_get_action_events_by_timesort devuelve objetos con:
 *   id, name, description, modulename, activityname, course { id, fullname },
 *   timesort (Unix timestamp del vencimiento), etc.
 */
const resolveEventType = (modulename = "") => {
  const mod = modulename.toLowerCase();
  if (["quiz", "assign", "workshop", "scorm", "lesson"].includes(mod)) return "Actividad";
  return "Evento";
};

/**
 * Normaliza la respuesta de core_calendar_get_action_events_by_timesort
 * al formato interno del componente.
 */
const normalizeEvents = (moodleEvents = []) =>
  moodleEvents.map((ev) => {
    const d = new Date(ev.timesort * 1000);
    const horas = String(d.getHours()).padStart(2, "0");
    const minutos = String(d.getMinutes()).padStart(2, "0");
    return {
      id: ev.coursemoduleid || ev.instance, // Prefer coursemoduleid for navigation
      titulo: ev.name,
      curso: ev.course?.fullname ?? ev.activityname ?? "",
      courseid: ev.course?.id ?? "",
      vencimiento: unixToIso(ev.timesort),
      hora: `${horas}:${minutos}`,
      tipo: resolveEventType(ev.modulename),
      modulename: ev.modulename,
    };
  });

/* ── Main component ─────────────────────────────────────────────────────── */

export const ActividadesYEventos = () => {
  const navigation = useNavigate();
  const moodle = useMoodleV2();

  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(moodle.calendarEvents === null);
  const [error, setError] = useState(null);

  const { colors, mode } = useTheme() || { colors: {}, mode: "light" };

  const isDark = mode === "dark";
  const bgMain = colors.backgroundColorBody || (isDark ? "#0f172a" : "#f8fafc");
  const bgCard = colors.backgroundColorDetailCourse || (isDark ? "#1e293b" : "#ffffff");
  const textPrimary = isDark ? "#f1f5f9" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const accent = colors.button_light || "#fdbf00";
  // const accent = "#ECB236" || "#fdbf00";

  // Eventos normalizados desde el contexto
  const actividades = normalizeEvents(moodle.calendarEvents ?? []);

  /* ── Fetch de datos ── */
  const fetchEvents = useCallback(async () => {
    if (!moodle.getCalendarEvents) return;
    setLoading(true);
    setError(null);
    try {
      await moodle.getCalendarEvents();
    } catch (err) {
      console.error("ActividadesYEventos:", err);
      setError("No se pudieron cargar los eventos. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [moodle.getCalendarEvents]);

  useEffect(() => {
    if (moodle.calendarEvents === null) {
      fetchEvents();
    }
  }, [moodle.calendarEvents]);

  /* ── Datos derivados ── */
  const datesWithEvents = actividades.map((a) => a.vencimiento);

  const filteredActividades = selectedDate ? actividades.filter((a) => a.vencimiento === selectedDate) : actividades;

  return (
    <>
      {/* ── Scoped styles ── */}
      <style>{`
        .aye-layout {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          flex-wrap: wrap;
        }
        .aye-list-panel {
          flex: 1 1 320px;
          min-width: 0;
        }
        .aye-calendar-panel {
          display: none;
        }
        @media (min-width: 768px) {
          .aye-calendar-panel {
            display: block;
            width: clamp(240px, 22vw, 300px);
            flex-shrink: 0;
          }
        }
        .aye-item:last-child {
          border-bottom: none !important;
        }
        .aye-item:hover {
          background-color: var(--aye-hover);
        }
        .aye-cal-btn {
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          border-radius: 6px;
          transition: background 0.15s;
        }
        .aye-cal-btn:hover {
          background-color: var(--aye-hover);
        }
        .aye-day-btn {
          background: none;
          border: none;
          cursor: pointer;
          border-radius: 6px;
          padding: 5px 0;
          font-size: 12px;
          font-weight: 500;
          transition: background 0.15s, color 0.15s;
          position: relative;
        }
        .aye-day-btn:hover {
          background-color: var(--aye-hover);
        }
      `}</style>

      <div
        style={{
          backgroundColor: bgMain,
          padding: "clamp(16px, 3vw, 36px) 0",
          fontFamily: "'Inter','Segoe UI',-apple-system,BlinkMacSystemFont,sans-serif",
          minHeight: "100%",
          "--aye-hover": isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
        }}
      >
        <div className="page-content-shell">
          {/* ── Título ── */}
          {/* <h2
            style={{
              fontSize: "clamp(18px, 2.5vw, 26px)",
              fontWeight: 800,
              color: readableColor(colors.backgroundColorBody, colors.textColorNavbar),
              margin: "0 0 20px 0",
              letterSpacing: "-0.03em",
              position: "relative",
              display: "inline-block",
            }}
          >
            Actividades y Eventos
          </h2> */}
          <div className="aye-layout">
            {/* ── Panel izquierdo: listado ── */}
            <div className="aye-list-panel">
              <div
                style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: 14,
                  backgroundColor: bgCard,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                {/* Franja de color */}
                <div
                  style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${accent}, ${accent}66)`,
                  }}
                />

                {/* Header del panel */}
                <div
                  style={{
                    padding: "14px 20px",
                    borderBottom: `1px solid ${borderColor}`,
                    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 4,
                      height: 18,
                      backgroundColor: accent,
                      borderRadius: 2,
                    }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>
                    {selectedDate ? `Actividades del ${formatDisplayDate(selectedDate)}` : "Próximas actividades"}
                  </span>

                  {/* Botón recargar */}
                  <button
                    onClick={fetchEvents}
                    disabled={loading}
                    title="Recargar"
                    style={{
                      marginLeft: "auto",
                      background: "none",
                      border: "none",
                      cursor: loading ? "default" : "pointer",
                      color: textSecondary,
                      display: "flex",
                      alignItems: "center",
                      padding: 4,
                      borderRadius: 6,
                      opacity: loading ? 0.4 : 1,
                    }}
                  >
                    <RefreshIcon style={{ fontSize: 16 }} />
                  </button>

                  {selectedDate && (
                    <button
                      onClick={() => setSelectedDate(null)}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: accent,
                        background: "none",
                        border: `1px solid ${accent}44`,
                        borderRadius: 20,
                        padding: "2px 10px",
                        cursor: "pointer",
                      }}
                    >
                      Ver todas
                    </button>
                  )}

                  {!selectedDate && !loading && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: textSecondary,
                        backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0",
                        borderRadius: 20,
                        padding: "2px 10px",
                      }}
                    >
                      {actividades.length} pendientes
                    </span>
                  )}
                </div>

                {/* Estados: cargando / error / vacío / lista */}
                {loading ? (
                  <div
                    style={{
                      padding: "40px 20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 14,
                    }}
                  >
                    <CircularProgress size={28} style={{ color: accent }} />
                    <span style={{ fontSize: 13, color: textSecondary }}>Cargando actividades…</span>
                  </div>
                ) : error ? (
                  <div style={{ padding: "32px 20px", textAlign: "center" }}>
                    <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 10 }}>{error}</p>
                    <button
                      onClick={fetchEvents}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: accent,
                        background: "none",
                        border: `1px solid ${accent}44`,
                        borderRadius: 20,
                        padding: "4px 14px",
                        cursor: "pointer",
                      }}
                    >
                      Reintentar
                    </button>
                  </div>
                ) : filteredActividades.length === 0 ? (
                  <div style={{ padding: "32px 20px", textAlign: "center" }}>
                    <CalendarMonthIcon style={{ fontSize: 36, color: textSecondary, opacity: 0.4 }} />
                    <p
                      style={{
                        fontSize: 13,
                        color: textSecondary,
                        marginTop: 10,
                        fontStyle: "italic",
                      }}
                    >
                      {selectedDate ? "Sin actividades para este día" : "No hay actividades próximas"}
                    </p>
                  </div>
                ) : (
                  <div style={{ overflowY: "scroll", maxHeight: "370.5px" }}>
                    {filteredActividades.map((item) => {
                      const urgencia = getDaysUntil(item.vencimiento);
                      const isActividad = item.tipo === "Actividad";
                      const tipoColor = isActividad ? "#3b82f6" : "#8b5cf6";

                      return (
                        <div
                          key={item.id}
                          className="aye-item hover:cursor-pointer"
                          onClick={() => {
                            moodle.setCurrentModules(null);
                            moodle.setIframeLoaded(false);
                            navigation(`/detailcourse/${item.courseid}?resource=${item.id}`);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            padding: "15px 20px",
                            borderBottom: `1px solid ${borderColor}`,
                            transition: "background-color 0.15s",
                          }}
                        >
                          {/* Icono tipo */}
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 10,
                              backgroundColor: `${tipoColor}18`,
                              border: `1px solid ${tipoColor}30`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {isActividad ? <AssignmentIcon style={{ fontSize: 18, color: tipoColor }} /> : <EventIcon style={{ fontSize: 18, color: tipoColor }} />}
                          </div>

                          {/* Texto */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                                marginBottom: 3,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: textPrimary,
                                  lineHeight: 1.3,
                                }}
                              >
                                {item.titulo}
                              </span>
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  padding: "2px 8px",
                                  borderRadius: 20,
                                  backgroundColor: `${tipoColor}18`,
                                  color: tipoColor,
                                  border: `1px solid ${tipoColor}30`,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.tipo}
                              </span>
                            </div>
                            {item.curso && (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: textSecondary,
                                  fontWeight: 500,
                                }}
                              >
                                {item.curso}
                              </span>
                            )}
                          </div>

                          {/* Fecha vencimiento */}
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                justifyContent: "flex-end",
                                marginBottom: 3,
                              }}
                            >
                              <AccessTimeIcon style={{ fontSize: 12, color: urgencia.color }} />
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: urgencia.color,
                                }}
                              >
                                {urgencia.label}
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: 11,
                                color: textSecondary,
                                fontWeight: 500,
                              }}
                            >
                              {formatDisplayDate(item.vencimiento)} a las {item.hora}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── Panel derecho: calendario ── */}
            <div className="aye-calendar-panel">
              <div
                style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: 14,
                  backgroundColor: bgCard,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                {/* Franja de color */}
                <div
                  style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${accent}, ${accent}66)`,
                  }}
                />

                {/* Header del calendario */}
                <div
                  style={{
                    padding: "14px 18px",
                    borderBottom: `1px solid ${borderColor}`,
                    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div style={{ width: 4, height: 18, backgroundColor: accent, borderRadius: 2 }} />
                  <span style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>Calendario</span>
                </div>

                <div style={{ padding: "16px 18px" }}>
                  <MiniCalendar
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    datesWithEvents={datesWithEvents}
                    accent={accent}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                    borderColor={borderColor}
                    isDark={isDark}
                  />
                </div>

                {/* Leyenda */}
                <div
                  style={{
                    padding: "12px 18px",
                    borderTop: `1px solid ${borderColor}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <LegendItem color="#3b82f6" label="Actividad" />
                  <LegendItem color="#8b5cf6" label="Evento" />
                  <LegendItem color={"#ECB236"} label="Fecha seleccionada" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Leyenda ─────────────────────────────────────────────────────────────── */

const LegendItem = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>{label}</span>
  </div>
);

/* ── Mini calendario ─────────────────────────────────────────────────────── */

function MiniCalendar({ selectedDate, setSelectedDate, datesWithEvents, accent, textPrimary, textSecondary, borderColor, isDark }) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Se ajusta para que el lunes sea el primer día de la semana (0) y el domingo el último (6)
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;

  const formatDate = (day) => {
    const mm = String(month + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${year}-${mm}-${dd}`;
  };

  const todayStr = today.toISOString().split("T")[0];

  return (
    <div>
      {/* Navegación de mes */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <button className="aye-cal-btn" onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} style={{ color: textSecondary }}>
          <ChevronLeftIcon style={{ fontSize: 18 }} />
        </button>

        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: textPrimary,
            textTransform: "capitalize",
          }}
        >
          {MESES[month]} {year}
        </span>

        <button className="aye-cal-btn" onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} style={{ color: textSecondary }}>
          <ChevronRightIcon style={{ fontSize: 18 }} />
        </button>
      </div>

      {/* Días de la semana */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 2,
          marginBottom: 6,
        }}
      >
        {DIAS_SEMANA.map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: 10,
              fontWeight: 800,
              color: textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              padding: "2px 0",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Días del mes */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 2,
        }}
      >
        {/* Celdas vacías */}
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {/* Días */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const fullDate = formatDate(day);
          const hasEvent = datesWithEvents.includes(fullDate);
          const isSelected = selectedDate === fullDate;
          const isToday = fullDate === todayStr;

          return (
            <button
              key={day}
              className="aye-day-btn"
              onClick={() => setSelectedDate(isSelected ? null : fullDate)}
              style={{
                color: isSelected ? "#fff" : isToday ? "#ECB236" : textPrimary,
                backgroundColor: isSelected ? "#ECB236" : "transparent",
                fontWeight: isToday || isSelected ? 800 : 500,
                border: isToday && !isSelected ? `1px solid #ECB23680` : "1px solid transparent",
              }}
            >
              {day}
              {/* Punto indicador de evento */}
              {hasEvent && !isSelected && (
                <span
                  style={{
                    position: "absolute",
                    bottom: 2,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "#ECB236",
                    display: "block",
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
